import clsx from 'clsx';
import { useEffect } from 'react';

/**
 * Responsive dialog shell. On phones it is a full-width bottom sheet that can
 * grow to the full (dynamic) viewport height and scrolls internally, so the
 * on-screen keyboard never pushes the Save button out of reach. On desktop it
 * is the familiar centered card.
 */
export function Modal({
  onClose,
  children,
  width = 'md',
  zIndex = 'z-[60]',
  className,
  bodyClassName,
  footer,
  title,
}: {
  onClose: () => void;
  children: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
  zIndex?: string;
  className?: string;
  bodyClassName?: string;
  /** Sticky action row pinned to the bottom (above the home indicator). */
  footer?: React.ReactNode;
  title?: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const widthClass =
    width === 'sm' ? 'sm:w-[460px]' : width === 'lg' ? 'sm:w-[640px]' : 'sm:w-[520px]';

  return (
    <div
      className={clsx(
        'fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center animate-[fadeIn_0.15s_ease]',
        zIndex,
      )}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={clsx(
          'bg-surface border border-border flex flex-col w-full max-h-[92dvh] sm:max-h-[90vh]',
          'rounded-t-2xl sm:rounded-xl sm:max-w-[90%] animate-[slideUp_0.2s_ease] overscroll-contain',
          widthClass,
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle, phones only */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0" aria-hidden>
          <span className="w-10 h-1 rounded-full bg-surface3" />
        </div>
        {title && (
          <div className="px-4 sm:px-6 pt-2 sm:pt-6 pb-2 flex items-center justify-between flex-shrink-0">
            <h2 className="text-base sm:text-lg font-bold">{title}</h2>
            <button
              className="btn btn-sm btn-ghost text-xl px-2 -mr-2 sm:hidden"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div
          className={clsx(
            'flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6',
            title ? 'pt-1' : 'pt-2 sm:pt-6',
            footer ? 'pb-3' : 'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6',
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer && (
          <div className="flex-shrink-0 border-t border-border px-4 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3 bg-surface sm:rounded-b-xl">
            <div className="flex justify-end gap-2 flex-wrap">{footer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
